import React, { useState } from 'react';
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../utils/firebase';
import { useNavigate } from 'react-router-dom';
import { toast, Toaster } from 'react-hot-toast';

// Import portal Firebase auth (when this file is created)
// You'll need to create this file based on the portalAuth from the client portal
import { portalAuth } from '../utils/portalFirebase';

// This URL would be used to redirect to the client portal
const PORTAL_URL = "https://portal.oiltank.app";

const UnifiedLogin = () => {
  const [activeTab, setActiveTab] = useState('inspectors'); // inspectors or clients
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const navigate = useNavigate();

  const handleLoginInspectors = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await signInWithEmailAndPassword(auth, email, password);
      toast.success('Successfully logged in!');
      navigate('/');
    } catch (error) {
      console.error('Login error:', error);
      // Provide more user-friendly error messages
      if (error.code === 'auth/wrong-password') {
        toast.error('Incorrect password. Please try again.');
      } else if (error.code === 'auth/user-not-found') {
        toast.error('No account found with this email.');
      } else if (error.code === 'auth/invalid-email') {
        toast.error('Please enter a valid email address.');
      } else if (error.code === 'auth/too-many-requests') {
        toast.error('Too many failed attempts. Please try again later.');
      } else {
        toast.error('Login failed. Please check your credentials.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLoginClients = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Validate the email format first
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        toast.error('Please enter a valid email address');
        setLoading(false);
        return;
      }
      
      // Validate password if provided
      if (password && password.length < 6) {
        toast.error('Password must be at least 6 characters');
        setLoading(false);
        return;
      }
      
      // Show a message indicating redirect
      toast.success('Redirecting to client portal...');
      
      // Create URL with email parameter
      let redirectUrl = `${PORTAL_URL}/login?email=${encodeURIComponent(email)}`;
      
      // Add password parameter if provided (for auto-login)
      if (password) {
        redirectUrl += `&password=${encodeURIComponent(password)}`;
      }
      
      // Optional: Log the redirect action (but not the credentials)
      console.log('Redirecting to client portal for user:', email);
      
      // Short delay for the toast to be visible
      setTimeout(() => {
        // Redirect to the portal login
        window.location.href = redirectUrl;
      }, 1000);
    } catch (error) {
      console.error('Redirect error:', error);
      toast.error('Failed to redirect to client portal. Please try again.');
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e?.preventDefault();
    if (!resetEmail) {
      toast.error('Please enter your email address');
      return;
    }

    setResetLoading(true);
    try {
      if (activeTab === 'inspectors') {
        await sendPasswordResetEmail(auth, resetEmail);
        toast.success('Password reset email sent! Please check your inbox.');
      } else {
        // For client portal, redirect to their forgot password page
        toast.success('Redirecting to client portal password reset...');
        setTimeout(() => {
          window.location.href = `${PORTAL_URL}/reset-password?email=${encodeURIComponent(resetEmail)}`;
        }, 1000);
        return;
      }
      setShowResetModal(false);
      setResetEmail('');
    } catch (error) {
      console.error('Reset password error:', error);
      if (error.code === 'auth/user-not-found') {
        toast.error('No account found with this email.');
      } else if (error.code === 'auth/invalid-email') {
        toast.error('Please enter a valid email address.');
      } else {
        toast.error('Failed to send reset email. Please try again.');
      }
    } finally {
      setResetLoading(false);
    }
  };

  const ResetPasswordModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">Reset Password</h3>
        <form onSubmit={handleForgotPassword}>
          <div className="mb-4">
            <label htmlFor="reset-email" className="block text-sm font-medium text-gray-700 mb-1">
              Email Address
            </label>
            <input
              id="reset-email"
              type="email"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              className="appearance-none relative block w-full px-4 py-3 border border-gray-300 placeholder-gray-400 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter your email"
              required
            />
          </div>
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => {
                setShowResetModal(false);
                setResetEmail('');
              }}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={resetLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {resetLoading ? 'Sending...' : 'Send Reset Link'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 fixed inset-0">
      <Toaster position="top-center" />
      <div className="max-w-md w-full mx-4 space-y-6 p-8 bg-white rounded-3xl shadow-xl border border-slate-100">
        <div className="flex flex-col items-center">
          <div className="w-24 h-24 bg-gradient-to-r from-blue-500 to-cyan-400 rounded-full flex items-center justify-center mb-4 shadow-lg">
            <img
              src="/oil.png"
              alt="OilTank.App"
              className="w-16 h-16 brightness-0 invert"
            />
          </div>
          <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-500 to-cyan-400 bg-clip-text text-transparent font-['Inter'] tracking-tight">
            OilTank.App
          </h2>
        </div>

        {/* Login Tabs */}
        <div className="flex rounded-lg overflow-hidden mb-4 border border-gray-200">
          <button
            className={`flex-1 py-3 text-sm font-medium ${
              activeTab === 'inspectors'
                ? 'bg-blue-500 text-white'
                : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
            }`}
            onClick={() => setActiveTab('inspectors')}
          >
            Inspectors Login
          </button>
          <button
            className={`flex-1 py-3 text-sm font-medium ${
              activeTab === 'clients'
                ? 'bg-blue-500 text-white'
                : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
            }`}
            onClick={() => setActiveTab('clients')}
          >
            Clients Portal
          </button>
        </div>

        <form 
          className="mt-8 space-y-6" 
          onSubmit={activeTab === 'inspectors' ? handleLoginInspectors : handleLoginClients}
        >
          <div className="space-y-4">
            <div>
              <label htmlFor="email-address" className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                id="email-address"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="appearance-none relative block w-full px-4 py-3 border border-gray-300 placeholder-gray-400 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required={activeTab === 'inspectors'} // Only required for inspector login
                className="appearance-none relative block w-full px-4 py-3 border border-gray-300 placeholder-gray-400 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center px-4 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-[#2B7FE8] hover:bg-[#2472d4] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {loading ? 'Signing in...' : 'Log In'}
          </button>
        </form>
        
        <div className="text-center">
          <button
            type="button"
            onClick={() => setShowResetModal(true)}
            className="text-sm text-blue-500 hover:text-blue-600 focus:outline-none focus:underline"
          >
            Forgot your password?
          </button>
        </div>

        {activeTab === 'inspectors' && (
          <div className="text-center text-sm text-gray-600 mt-4">
            <p>
              Interested in creating reports?{' '}
              <a 
                href="mailto:leo@makeasmartchoice.us" 
                className="text-blue-500 hover:text-blue-600 hover:underline"
              >
                Email us
              </a>
            </p>
          </div>
        )}

        {activeTab === 'clients' && (
          <div className="text-center text-sm text-gray-600">
            <p>
              Don't have a client account?{' '}
              <a 
                href={`${PORTAL_URL}/register`} 
                className="text-blue-500 hover:text-blue-600 hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                Create an account
              </a>
            </p>
          </div>
        )}
      </div>
      {showResetModal && <ResetPasswordModal />}
    </div>
  );
};

export default UnifiedLogin; 